import { usePageView } from '@/analytics/pageTypes';
import { SeoHeader } from '@/components/SeoHeader';
import { Layout } from '@/components/layout';
import Hero from '@/sections/Hero';
import { getIntlProps, type IntlProps } from '@/utils/intl';
import type { GetStaticProps } from 'next';
import { useTranslation } from 'react-i18next';
import { SectionDelimiter } from '@/components/SectionDelimiter';
import React, { useRef } from 'react';
import { MoreInfo } from '@/sections/retention/MoreInfo';
import { LINKS } from '@/constants/links';
import { FaqIntro } from '@/sections/FaqIntro';
import { Benefits } from '@/sections/retention/Benefits';
import { getPageBannerBySlug, PageBanner } from '@/api/sanity/pageBanners';
import { Faqs } from '@/sections/retention/Faqs';
import { Dissatisfaction } from '@/sections/retention/Dissatisfaction';
import { Contact } from '@/sections/retention/Contact';
import { isAllowedRoute } from '@/lang/pagesConfig';
import { getAllPageMenuPromoBanners, PageMenuPromoBanner } from '@/api/sanity/pageMenuPromoBanner';
interface Props extends IntlProps {
  pageBanner: PageBanner | null;
  menuPromoBanners?: PageMenuPromoBanner[];
}
export default function Page({
  menuPromoBanners,
  pageBanner
}: Props) {
  const {
    t
  } = useTranslation(['retention', 'core']);
  usePageView('retention');
  const benefitsSectionRef = useRef<HTMLDivElement>(null);
  const contactSectionRef = useRef<HTMLDivElement>(null);
  const handleScrollToBenefits = () => {
    if (benefitsSectionRef.current) {
      benefitsSectionRef.current.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const handleScrollToContact = () => {
    if (contactSectionRef.current) {
      contactSectionRef.current.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <Layout menuPromoBanners={menuPromoBanners}>
			<SeoHeader title={t('seo.title')} description={t('seo.description')} />
			<div className="relative">
				<Hero videoId={pageBanner?.videoId} title={pageBanner?.title ?? t('banner.title')} text={pageBanner?.text ?? t('banner.text')} buttons={pageBanner ? pageBanner.buttons?.length ? [{
        ...pageBanner.buttons[0],
        to: undefined,
        onClick: handleScrollToBenefits
      }, pageBanner.buttons[1]].filter(Boolean) : null : [{
        ariaLabel: t('banner.cta'),
        label: t('banner.cta'),
        onClick: handleScrollToBenefits,
        testId: 'retentionPrimaryCTA',
        analyticsParam: 'retentionPrimaryCTA',
        variant: 'primary'
      }]} image={{
        url: pageBanner?.image?.url ?? '/images/sections/retention/hero/hero.png',
        alt: pageBanner?.image?.alt ?? t('banner.title')
      }} variant={pageBanner?.variant || 'mascots'} />

				<div className="bg-blue-50">
					<SectionDelimiter color="bg-blue-700" background="bg-transparent" />
				</div>

				<div className="relative z-30 bg-blue-700 pt-10 pb-20 lg:pt-20 lg:pb-40">
					<div className="bgCircle absolute bottom-10 left-1/4 hidden -translate-x-1/4 rotate-90 lg:block" />
					<div className="bgCircle absolute top-10 right-1/4 hidden h-96 w-96 translate-x-1/4 rotate-90 xl:block" />
					<div className="relative z-10">
						<Dissatisfaction handleScrollToContact={handleScrollToContact} />
					</div>
				</div>

				<SectionDelimiter color="bg-blue-100" background="bg-blue-700" />

				<div className="relative bg-blue-100 pt-10 pb-20 lg:pt-20 lg:pb-40">
					<div className="flex flex-col gap-20 lg:gap-40">
						<div className="scroll-mt-20" ref={benefitsSectionRef}>
							<Benefits />
						</div>
						<div className="relative overflow-x-hidden">
							<div className="bgCircle absolute top-0 right-0 h-72! w-72! translate-x-1/2 lg:top-1/4 lg:right-auto lg:left-2/3 lg:-translate-x-1/2" />
							<div className="relative z-10 scroll-mt-20" ref={contactSectionRef}>
								<Contact />
							</div>
						</div>
						<div className="relative overflow-x-hidden">
							<div className="bgCircle absolute top-0 right-0 h-72! w-72! translate-x-1/2 -rotate-90 lg:hidden" />
							<div className="relative z-10">
								<Faqs />
							</div>
						</div>
					</div>
					<div className="bgCircle absolute bottom-0 left-0 -translate-x-1/2 lg:left-1/4" />
				</div>

				<SectionDelimiter color="bg-blue-50" background="bg-blue-100" />

				<div className="relative overflow-hidden bg-blue-50 py-10 lg:py-20">
					<div className="relative z-10 flex flex-col gap-20 lg:gap-40">
						<FaqIntro link={LINKS().faq} text={t('faq.text')} />
						<MoreInfo />
					</div>
					<div className="bgCircle absolute right-0 bottom-0 translate-x-1/4 translate-y-1/4 rotate-180" />
				</div>
			</div>
		</Layout>;
}
export const getStaticProps = (async ctx => {
  const isAllowed = isAllowedRoute('zruseni-skip-pay');
  if (!isAllowed) {
    return {
      notFound: true
    };
  }
  return {
    props: {
      pageBanner: await getPageBannerBySlug('retention'),
      menuPromoBanners: await getAllPageMenuPromoBanners(),
      ...(await getIntlProps(ctx, ['core', 'retention', 'personalDataRequests']))
    }
  };
}) satisfies GetStaticProps<Props>;